import React, { useEffect, useRef, useState } from 'react';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { useParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Card,
  CardBody,
  Button,
  Label,
  CustomInput,
  Alert,
  Badge,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import Input from '~/components/Inputs/Input';
import Loading from '~/components/Loading';
import PageHeader from '~/components/Common/PageHeader';
import api from '~/services/api';
import history from '~/services/history';

/**
 * Eventos suportados pelo webhook. Manter sincronizado com
 * PartnerWebhook::$supportedEvents no backend e com EVENT_LABEL
 * do PartnerWebhookCard.js.
 *
 * Agrupamento visual apenas (não afeta o payload enviado ao backend).
 */
const AVAILABLE_EVENTS = [
  {
    group: 'Ciclo de vida da venda',
    events: [
      { value: 'cart.created', label: 'cart.created — venda criada' },
      {
        value: 'cart.in_fraud_analysis',
        label: 'cart.in_fraud_analysis — entrou em análise antifraude',
      },
      { value: 'cart.paid', label: 'cart.paid — pagamento confirmado' },
      {
        value: 'cart.in_delivery',
        label: 'cart.in_delivery — em transporte',
      },
      {
        value: 'cart.completed',
        label: 'cart.completed — concluída (entregue)',
      },
      { value: 'cart.cancelled', label: 'cart.cancelled — cancelada' },
      {
        value: 'cart.payment_rejected',
        label: 'cart.payment_rejected — pagamento não aprovado',
      },
      {
        value: 'cart.fraud_rejected',
        label: 'cart.fraud_rejected — reprovada pela antifraude',
      },
      {
        value: 'cart.awaiting_refund',
        label: 'cart.awaiting_refund — estorno solicitado',
      },
      { value: 'cart.refunded', label: 'cart.refunded — estorno processado' },
      {
        value: 'cart.chargeback',
        label: 'cart.chargeback — chargeback recebido',
      },
      {
        value: 'cart.returned',
        label: 'cart.returned — devolvida pelo cliente',
      },
    ],
  },
  {
    group: 'Entregas',
    events: [
      {
        value: 'delivery.status_changed',
        label: 'delivery.status_changed — mudou de status',
      },
    ],
  },
];

/**
 * Lista plana dos eventos válidos, derivada de AVAILABLE_EVENTS.
 * Usada pra validação Yup e checagem de events desconhecidos.
 */
const ALL_EVENT_VALUES = AVAILABLE_EVENTS.flatMap(g =>
  g.events.map(e => e.value)
);

const ENV_LABEL = {
  test: { label: 'TESTE', color: '#6b7280' },
  live: { label: 'PROD', color: '#2544b1' },
};

const statusBadge = webhook => {
  if (webhook?.auto_disabled_at) {
    return { label: 'Auto-desativado', bg: '#ef4444' };
  }
  if (!webhook?.enabled) {
    return { label: 'Desativado', bg: '#9ca3af' };
  }
  return { label: 'Ativo', bg: '#10b981' };
};

/**
 * Formata timestamp ISO em "dd/MM/yyyy 'às' HH:mm" ou retorna
 * string vazia se não vier valor.
 */
const formatIsoDate = value => {
  if (!value) return '—';
  try {
    return format(parseISO(value), "dd/MM/yyyy 'às' HH:mm", {
      locale: ptBR,
    });
  } catch {
    return value;
  }
};

const EditPartnerWebhook = () => {
  const { webhook_id } = useParams();
  const formRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhook, setWebhook] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [eventsError, setEventsError] = useState(null);
  const [enabled, setEnabled] = useState(true);

  const baseUrl = `/administrator/partner-webhooks/${webhook_id}`;

  // -----------------------------
  // Load inicial
  // -----------------------------
  const fillFormFromWebhook = wh => {
    setWebhook(wh);
    setSelectedEvents(wh.events || []);
    setEnabled(Boolean(wh.enabled));

    // Campos url/description são populados via prop `initialData` do <Form>,
    // que é lida quando o form monta. Como o form só renderiza depois de
    // `loading` virar false (e nesse ponto webhook já está setado), a
    // primeira renderização do form já vem com os valores corretos.
    //
    // Se este método for chamado DEPOIS do form já estar montado (ex: após
    // um save que recarrega o webhook fresh do backend), usa setData
    // manualmente pra atualizar os inputs.
    if (formRef.current) {
      formRef.current.setData({
        url: wh.url || '',
        description: wh.description || '',
      });
    }
  };

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(baseUrl);
        if (ignore) return;

        // O backend pode envelopar em { data: {...} } ou retornar direto.
        // Aceita ambas as formas.
        const wh = data?.data || data;
        fillFormFromWebhook(wh);
      } catch (err) {
        toast.error(
          err?.response?.data?.message || 'Não foi possível carregar o webhook.'
        );
        history.push('/webhooks-parceiros');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhook_id]);

  // -----------------------------
  // Toggle de evento (checkbox)
  // -----------------------------
  const toggleEvent = eventValue => {
    setEventsError(null);
    setSelectedEvents(prev => {
      if (prev.includes(eventValue)) {
        return prev.filter(e => e !== eventValue);
      }
      return [...prev, eventValue];
    });
  };

  // -----------------------------
  // Submit
  // -----------------------------
  const handleSubmit = async data => {
    try {
      formRef.current.setErrors({});
      setEventsError(null);

      const schema = Yup.object().shape({
        url: Yup.string()
          .required('A URL é obrigatória.')
          .url('URL inválida.')
          .matches(/^https:\/\//i, 'A URL deve usar HTTPS.')
          .max(500, 'URL não pode exceder 500 caracteres.'),
        description: Yup.string()
          .nullable()
          .max(500, 'Descrição não pode exceder 500 caracteres.'),
      });

      await schema.validate(data, { abortEarly: false });

      if (selectedEvents.length === 0) {
        setEventsError('Selecione pelo menos um evento.');
        return;
      }

      // Verifica se algum evento selecionado não é válido — não
      // deveria acontecer via UI, mas defensivo caso array antigo do
      // banco tenha valor removido do enum.
      const invalid = selectedEvents.filter(e => !ALL_EVENT_VALUES.includes(e));
      if (invalid.length > 0) {
        setEventsError(
          `Eventos inválidos: ${invalid.join(', ')}. Remova-os antes de salvar.`
        );
        return;
      }

      setSaving(true);

      const payload = {
        url: data.url?.trim(),
        description: data.description?.trim() || null,
        events: selectedEvents,
        enabled,
      };

      const { data: updated } = await api.patch(baseUrl, payload);
      const wh = updated?.data || updated;

      toast.success('Webhook atualizado com sucesso!');
      fillFormFromWebhook(wh);
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach(e => {
          validationErrors[e.path] = e.message;
        });
        formRef.current.setErrors(validationErrors);
        return;
      }
      if (err?.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('Não foi possível salvar as alterações.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!webhook) {
    return null;
  }

  const status = statusBadge(webhook);
  const env =
    ENV_LABEL[webhook?.partner_application?.environment] || ENV_LABEL.test;

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="EDITAR WEBHOOK"
          backTo="/webhooks-parceiros"
          responsivePosition="start"
          desktopPosition="center"
        />

        <Form
          ref={formRef}
          onSubmit={handleSubmit}
          initialData={{
            url: webhook?.url || '',
            description: webhook?.description || '',
          }}
        >
          <Row>
            <Col lg={8}>
              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-3">Configuração</h5>

                  <FormGroup>
                    <Label for="url">URL do endpoint</Label>
                    <Input
                      name="url"
                      id="url"
                      placeholder="https://parceiro.com.br/webhook"
                      style={{
                        fontFamily:
                          '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                      }}
                    />
                    <small className="text-muted">
                      Deve ser HTTPS. Endpoint que receberá os payloads dos
                      eventos assinados.
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label for="description">Descrição (opcional)</Label>
                    <Input
                      name="description"
                      id="description"
                      placeholder="Ex: webhook de produção — pedidos"
                    />
                  </FormGroup>

                  <FormGroup className="mb-0">
                    <div className="d-flex align-items-center">
                      <CustomInput
                        type="switch"
                        id="enabled-switch"
                        checked={enabled}
                        onChange={e => setEnabled(e.target.checked)}
                        label={enabled ? 'Ativo' : 'Desativado'}
                      />
                    </div>
                    <small className="text-muted">
                      Desativar pausa a entrega dos eventos sem apagar a
                      configuração. Reative a qualquer momento.
                    </small>
                  </FormGroup>
                </CardBody>
              </Card>

              <Card className="mb-3">
                <CardBody>
                  <h5 className="mb-2">Eventos</h5>
                  <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                    Selecione os eventos que este webhook deve receber. O Viddy
                    só envia os eventos assinados aqui — os demais são ignorados
                    pra este endpoint.
                  </p>

                  {eventsError && (
                    <Alert color="danger" className="py-2">
                      {eventsError}
                    </Alert>
                  )}

                  {AVAILABLE_EVENTS.map(group => (
                    <div key={group.group} className="mb-3">
                      <Label
                        className="font-weight-600 mb-2"
                        style={{ fontSize: '13px' }}
                      >
                        {group.group}
                      </Label>
                      {group.events.map(ev => (
                        <CustomInput
                          key={ev.value}
                          type="checkbox"
                          id={`event-${ev.value}`}
                          checked={selectedEvents.includes(ev.value)}
                          onChange={() => toggleEvent(ev.value)}
                          label={
                            <span
                              style={{
                                fontFamily:
                                  '"JetBrains Mono", Menlo, Monaco, Consolas, monospace',
                                fontSize: '12px',
                              }}
                            >
                              {ev.label}
                            </span>
                          }
                          className="mb-1"
                        />
                      ))}
                    </div>
                  ))}
                </CardBody>
              </Card>

              <div className="d-flex justify-content-end mb-4">
                <Button
                  type="button"
                  color="secondary"
                  className="mr-2"
                  onClick={() => history.push('/webhooks-parceiros')}
                  disabled={saving}
                >
                  Cancelar
                </Button>
                <Button type="submit" color="primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar alterações'}
                </Button>
              </div>
            </Col>

            <Col lg={4}>
              <Card className="mb-3">
                <CardBody>
                  <h6 className="mb-3">Informações</h6>

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Aplicação
                    </Label>
                    <div className="font-weight-600">
                      {webhook?.partner_application?.name || '—'}
                    </div>
                    <Badge
                      style={{
                        backgroundColor: env.color,
                        color: '#fff',
                        fontSize: 9,
                        padding: '2px 6px',
                        marginTop: 2,
                      }}
                    >
                      {env.label}
                    </Badge>
                  </div>

                  <hr />

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Status atual
                    </Label>
                    <div>
                      <Badge
                        style={{
                          backgroundColor: status.bg,
                          color: '#fff',
                          fontSize: 10,
                          padding: '4px 8px',
                        }}
                      >
                        {status.label}
                      </Badge>
                    </div>
                  </div>

                  {webhook.auto_disabled_at && (
                    <Alert color="warning" className="py-2 mt-2 mb-2">
                      <small>
                        Auto-desativado em{' '}
                        <strong>
                          {formatIsoDate(webhook.auto_disabled_at)}
                        </strong>{' '}
                        após 15 falhas consecutivas. Use o botão
                        &quot;Reativar&quot; na lista de webhooks pra retomar as
                        entregas.
                      </small>
                    </Alert>
                  )}

                  <hr />

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Falhas consecutivas
                    </Label>
                    <div>{webhook.consecutive_failures || 0}</div>
                  </div>

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Última entrega com sucesso
                    </Label>
                    <div>{formatIsoDate(webhook.last_success_at)}</div>
                  </div>

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Última falha
                    </Label>
                    <div>{formatIsoDate(webhook.last_failure_at)}</div>
                  </div>

                  <hr />

                  <div className="mb-2">
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Criado em
                    </Label>
                    <div>{formatIsoDate(webhook.created_at)}</div>
                  </div>

                  <div>
                    <Label className="text-muted mb-1" style={{ fontSize: 12 }}>
                      Última atualização
                    </Label>
                    <div>{formatIsoDate(webhook.updated_at)}</div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </div>
  );
};

export default EditPartnerWebhook;
